import { useQuery } from "@tanstack/react-query";
import {
  authenticatedGet,
  authenticatedPost,
  authenticatedPut,
  authenticatedDelete,
} from "../../api/authenticatedFetch";

const BASE = "/api/v1/service-accounts";

// listServiceAccounts returns the tenant's service accounts: label, assigned
// role codes, status (active/suspended), last-used and created timestamps.
// Never contains a token — the raw secret is only ever returned once, at
// creation or rotation time (see createServiceAccount / rotateToken below).
export const listServiceAccounts = async () => {
  const res = await authenticatedGet(BASE);
  return res?.service_accounts ?? [];
};

// createServiceAccount mints a new non-interactive principal with the given
// roles. Response carries the one-time raw secret under `token` alongside the
// created `service_account` — the backend rejects (409
// SERVICE_ACCOUNT_FORBIDDEN_CAPABILITY) a role set whose effective
// capabilities include an identity/admin capability a service account may
// not hold.
export const createServiceAccount = async ({ label, roleCodes }) => {
  return authenticatedPost(BASE, { label, roleCodes });
};

// rotateToken revokes the service account's current active token and mints a
// new one. Response carries the new raw secret under `token`, shown once.
export const rotateToken = async (id) => {
  return authenticatedPost(`${BASE}/${id}/rotate-token`, {});
};

// revokeToken revokes the service account's current active token without
// minting a replacement — the service account can no longer authenticate
// until a new token is rotated in.
export const revokeToken = async (id) => {
  return authenticatedPost(`${BASE}/${id}/revoke-token`, {});
};

// updateServiceAccount edits the label and/or role assignment. Re-runs the
// same forbidden-capability guard as create.
export const updateServiceAccount = async (id, body) => {
  const res = await authenticatedPut(`${BASE}/${id}`, body);
  return res?.service_account ?? res;
};

// deleteServiceAccount deactivates the service account (status -> 'suspended').
export const deleteServiceAccount = async (id) => {
  return authenticatedDelete(`${BASE}/${id}`);
};

// --- react-query hooks ---

export function useServiceAccounts() {
  return useQuery(["service-accounts"], listServiceAccounts, {
    refetchOnWindowFocus: false,
  });
}
